import React, { useEffect, useState } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import MainTitle from '../../../components/ui/MainTitle';
import SubTitle from '../../../components/ui/SubTitle';
import ProductForm from '../../../components/store/products/ProductForm';
import SubmitButton from '../../../components/form/SubmitButton';
import ButtonSelect from '../../../components/form/ButtonSelect';
import ImageUploaderUpdate, { ExistingImage } from '../../../components/store/products/ImageUploaderUpdate';
import { useProductCategories } from '../../../hooks/store/productCategory';
import { useNavigate, useParams } from 'react-router-dom';
import { useSingleProductDetail } from '../../../hooks/store/product';
import LoadingErrorState from '../../../components/ui/LoadingErrorState';
import { useUpdateResource } from '../../../hooks/useUpdateResource';
import { useTranslation } from 'react-i18next';
import LoadingButton from '../../../components/form/LoadingButton';
import { formatNumber } from '../../../utils/formatNumber';

type FormValues = {
  productNameEn: string;
  productNameAr: string;
  shortDescriptionEn: string;
  shortDescriptionAr: string;
  longDescriptionEn: string;
  longDescriptionAr: string;
  price: number;
};

export default function UpdateProduct() {
const [newImages, setNewImages] = useState<File[]>([]);

  const { productId,storeId} = useParams<{ productId: string, storeId:string}>();
  const numericProductId = productId ? parseInt(productId) : 0;
  const numericStoreId = storeId ? parseInt(storeId) : undefined;

    const { data: product, isLoading, isError } = useSingleProductDetail(numericProductId, numericStoreId);

    const [selectedCategory, setSelectedCategory] = useState<number | null>(0);
    const [existingImages, setExistingImages] = useState<ExistingImage[]>([]);
    const [selectedPrepTime, setSelectedPrepTime] = useState<number | null>(null);

    useEffect(() => {
      if (product) {
        setSelectedCategory(product.category?.id || 0);
        setExistingImages(
          product.images?.map((im) => ({ imageUrl: im })) || []
        );
        setSelectedPrepTime(product.preparationTime || null);
      }
    }, [product]);

  
    const{data:categories} = useProductCategories(numericStoreId);
  

  const { register, handleSubmit, formState: { errors } } = useForm<FormValues>();

  const {loading , updateResource} = useUpdateResource();
  const navigate = useNavigate();
  const {t} = useTranslation();

  const prepTimes = [
    { id: 5, label: `${formatNumber(5)} ${t('mins')}` },
    { id: 10, label: `${formatNumber(10)}  ${t('mins')}` },
    { id: 15, label: `${formatNumber(15)}  ${t('mins')}` },
    { id: 20, label:  `${formatNumber(20)}  ${t('mins')}` },
    { id: 25, label:  `${formatNumber(25)}  ${t('mins')}` },
    { id: 30, label:  `${formatNumber(30)}  ${t('mins')}` },
  ];

  const onSubmit: SubmitHandler<FormValues> = (data) => {
    if (selectedCategory === null || selectedPrepTime === null) {
      alert("Please select category and preparation time");
      return;
    }

    const languages = [
    {
      languageCode: "en",
      name: data.productNameEn,
      shortDescription: data.shortDescriptionEn,
      longDescription: data.longDescriptionEn,
    },
    {
      languageCode: "ar",
      name: data.productNameAr,
      shortDescription: data.shortDescriptionAr,
      longDescription: data.longDescriptionAr,
    },
  ];
  const formData = new FormData();
  formData.append("preparationTime", String(selectedPrepTime));
  formData.append("basePrice", String(data.price));
  formData.append("categoryId", String(selectedCategory));
  formData.append("languages", JSON.stringify(languages));
  formData.append("existingImages", JSON.stringify(existingImages));
  newImages.forEach((file, index) => {
    formData.append("images", file);
  });
  const endPoint =  numericStoreId ? `product/update/${numericProductId}?storeId=${numericStoreId}` :`product/update/${numericProductId}`
  updateResource(endPoint, formData, numericStoreId?"admin" : "store",{
    method: "PUT",
    successMessage: t("store_created_success"),
    invalidateQueries:["product-detail"],
    navigateAfter: () => navigate(numericStoreId? `/admin/stores/${numericStoreId}/products/${numericProductId}` :  `/store/products/${numericProductId}`),
  });
  };

  return (
    <div className="container p-6 h-[100vh] overflow-auto">
      <div className="mb-14">
        <MainTitle title="update"/>
      </div>

      <LoadingErrorState
      isError={isError}
      isLoading={isLoading}
      />
      {
        product &&
        <>
        <div className="mb-14">
        <SubTitle title="product_images" hint="max_5_images"/>
        <ImageUploaderUpdate
            existingImages={existingImages}
            setExistingImages={setExistingImages}
            newImages={newImages}
            setNewImages={setNewImages}
            maxImages={5}
            label="add_photo"
            />
      </div>

      <div className="mb-6">
        <SubTitle title="product_details"/>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 max-w-[800px]">
          <ProductForm
            register={register}
            errors={errors}
            defaultValues={{
                productNameEn:product.languages.find(ln=>ln.languageCode==="en")?.name ||"",
                productNameAr: product.languages.find(ln=>ln.languageCode==="ar")?.name ||"",
                shortDescriptionEn: product.languages.find(ln=>ln.languageCode==="en")?.shortDescription ||"",
                shortDescriptionAr: product.languages.find(ln=>ln.languageCode==="ar")?.shortDescription ||"",
                longDescriptionEn: product.languages.find(ln=>ln.languageCode==="en")?.longDescription ||"",
                longDescriptionAr:product.languages.find(ln=>ln.languageCode==="ar")?.longDescription ||"",
                price: product.basePrice,
            }}
            />


            <div>
            <SubTitle title='select_category'/>
            <ButtonSelect
            options={categories?categories.map(c=>({id:c.id,label:c.languages[0].title})):[]}
            selected={selectedCategory}
            onSelect={setSelectedCategory}
            className="mb-4"
          />
          </div>

          <div>
            <SubTitle title='select_preparation_time'/>
            <ButtonSelect
            options={prepTimes}
            selected={selectedPrepTime}
            onSelect={setSelectedPrepTime}
            className="mb-4"
          />
          </div>
          {
            loading
            ?
            <LoadingButton/>
            :
            <SubmitButton label='update'/>
          }
        </form>
      </div>
        </>
      }
    </div>
  );
}
